import { useEffect, useState } from "react";
import { getAttraction } from "@/lib/attractionsApi";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { ChevronDown, ChevronUp } from "lucide-react";

interface AttractionViewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  attractionId?: number;
}

const AttractionViewModal = ({ open, onOpenChange, attractionId }: AttractionViewModalProps) => {
  const { toast } = useToast();
  const [attraction, setAttraction] = useState<any>(null);
  const [tickets, setTickets] = useState<any[]>([]);
  const [expandedTickets, setExpandedTickets] = useState<Record<number, boolean>>({});

  useEffect(() => {
    if (open && attractionId) {
      loadAttractionData();
    }
  }, [open, attractionId]);

  const loadAttractionData = async () => {
    if (!attractionId) return;
    
    try {
      const data = await getAttraction(attractionId);
      setAttraction(data);
      setTickets(data.tickets || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const formatPricingType = (type: string) => {
    const types: Record<string, string> = {
      'per_person': 'Per Person',
      'per_family': 'Per Family',
      'per_group': 'Per Group'
    };
    return types[type] || type;
  };

  const toggleTicketExpanded = (index: number) => {
    setExpandedTickets((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  if (!attraction) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>View Attraction Details</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {attraction.image_url && (
            <div className="w-full">
              <img 
                src={attraction.image_url} 
                alt={attraction.title}
                className="w-full h-48 object-cover rounded-lg"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Attraction Code</Label>
              <Input value={attraction.attraction_code || ""} disabled />
            </div>
            <div className="space-y-2">
              <Label>Supplier Name</Label>
              <Input value={attraction.supplier_name || ""} disabled />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Title</Label>
            <Input value={attraction.title || ""} disabled />
          </div>

          <div className="space-y-2">
            <Label>Description</Label>
            <Textarea value={attraction.description || ""} disabled rows={4} />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Opening Hours</Label>
              <Input value={attraction.opening_hours || ""} disabled />
            </div>
            <div className="space-y-2">
              <Label>Tax Percentage</Label>
              <Input value={attraction.tax_percentage?.toString() || ""} disabled />
            </div>
          </div>

          <Separator />

          {tickets.length > 0 && (
            <div className="space-y-3">
              <h3 className="text-lg font-semibold">Attraction Tickets</h3>
              {tickets.map((ticket, index) => (
                <Collapsible
                  key={ticket.id}
                  open={expandedTickets[index]}
                  onOpenChange={() => toggleTicketExpanded(index)}
                >
                  <div className="border rounded-lg overflow-hidden">
                    <CollapsibleTrigger className="w-full p-4 hover:bg-muted/50 transition-colors">
                      <div className="flex items-center gap-3">
                        <div className="flex-1 text-left">
                          <div className="font-medium">{ticket.title}</div>
                          <div className="text-sm text-muted-foreground">
                            {formatPricingType(ticket.pricing_type)}
                          </div>
                        </div>
                        {expandedTickets[index] ? (
                          <ChevronUp className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                    </CollapsibleTrigger>
                    <CollapsibleContent className="px-4 pb-4 space-y-4 animate-accordion-down">
                      <Separator />
                      
                      {ticket.description && (
                        <div className="space-y-2">
                          <Label>Description</Label>
                          <p className="text-sm text-muted-foreground">{ticket.description}</p>
                        </div>
                      )}

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>Cost Supplier</Label>
                          <Input value={ticket.cost_supplier || ""} disabled />
                        </div>
                        <div className="space-y-2">
                          <Label>Website Price</Label>
                          <Input value={ticket.website_price || ""} disabled />
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-4">
                        <div className="space-y-2">
                          <Label>Price OP</Label>
                          <Input value={ticket.price_op || ""} disabled />
                        </div>
                        <div className="space-y-2">
                          <Label>Price AG</Label>
                          <Input value={ticket.price_ag || ""} disabled />
                        </div>
                        <div className="space-y-2">
                          <Label>Price CF</Label>
                          <Input value={ticket.price_cf || ""} disabled />
                        </div>
                      </div>
                    </CollapsibleContent>
                  </div>
                </Collapsible>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AttractionViewModal;
